"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, PhoneCall, ShieldCheck, Sparkles, FlaskConical } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
export default function PatCta() {
  const shouldReduceMotion = useReducedMotion();
  const keyBenefits = [{
    title: "Custom Feasibility Studies",
    description: "Sample testing and proof-of-concept evaluations before deployment.",
    icon: FlaskConical
  }, {
    title: "GMP & Lifecycle Compliance",
    description: "Full DQ/IQ/OQ qualification protocols and regulated audit support.",
    icon: ShieldCheck
  }, {
    title: "Direct Regional Support",
    description: "Dedicated application specialists across Ireland and the UK.",
    icon: CheckCircle2
  }];
  return <section id="patapplicationcta" data-nav="light" className="bg-background py-16 md:py-24">
      <div className="page-container">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: "-50px"
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }}>
          <Card className="relative overflow-hidden border-border bg-gradient-to-br from-secondary/80 via-card to-secondary/30 shadow-lg md:p-4">
            <div className="absolute -top-24 -right-24 size-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 size-96 rounded-full bg-chart-2/10 blur-3xl pointer-events-none" />

            <CardContent className="relative z-10 p-6 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                {/* Left content column */}
                <div className="lg:col-span-7 space-y-6">
                  <div className="space-y-3">
                    <Badge variant="secondary" className="px-3 py-1 gap-1.5 text-xs font-semibold">
                      <Sparkles className="size-3.5 text-primary" />
                      Process Analytical Technology Support
                    </Badge>
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
                      Discuss Your Specific PAT Application
                    </h2>
                  </div>

                  <p className="text-base md:text-lg text-muted-foreground font-sans leading-relaxed max-w-2xl">
                    Speak directly with our analytical technology team to evaluate your real-time process monitoring, blend uniformity, drying endpoints, or raw material verification requirements.
                  </p>

                  {/* Highlights Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                    {keyBenefits.map((benefit, i) => {
                    const Icon = benefit.icon;
                    return <div key={benefit.title} data-index={i} className="flex flex-col gap-2 rounded-lg border border-border/70 bg-card/60 p-3.5 shadow-2xs backdrop-blur-xs">
                          <div className="flex items-center gap-2">
                            <div className="flex size-7 items-center justify-center rounded-md bg-primary/10 text-primary">
                              <Icon className="size-4" />
                            </div>
                            <span className="text-sm font-semibold text-foreground leading-tight">
                              {benefit.title}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground font-sans leading-normal">
                            {benefit.description}
                          </p>
                        </div>;
                  })}
                  </div>
                </div>

                {/* Right CTA box */}
                <div className="lg:col-span-5 flex flex-col justify-center">
                  <div className="rounded-2xl border border-border bg-card p-6 md:p-8 shadow-md space-y-6">
                    <div className="space-y-2">
                      <h3 className="text-xl font-default font-semibold text-foreground">
                        Ready to optimize your process?
                      </h3>
                      <p className="text-sm text-muted-foreground font-sans">
                        Our technical consultants help configure the ideal probe, spectrometer, and compliance framework for your lab or plant.
                      </p>
                    </div>

                    <div className="space-y-3 pt-2">
                      <Button asChild size="lg" className="w-full text-base font-semibold shadow-sm group">
                        <Link to="/DiscussYourApplicationPage" className="flex items-center justify-center gap-2">
                          <span>Discuss Your Application</span>
                          <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>

                      <div className="grid grid-cols-2 gap-3">
                        <Button asChild variant="outline" size="default" className="w-full text-xs sm:text-sm font-medium">
                          <Link to="/BookADemoPage">
                            Book a Demo
                          </Link>
                        </Button>
                        <Button asChild variant="outline" size="default" className="w-full text-xs sm:text-sm font-medium">
                          <Link to="/FeasibilityStudiesPage">
                            Feasibility Study
                          </Link>
                        </Button>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground font-sans">
                      <div className="flex items-center gap-1.5">
                        <PhoneCall className="size-3.5 text-primary" />
                        <span>Direct technical response</span>
                      </div>
                      <Link to="/ContactPage" className="text-primary hover:underline font-medium">
                        Contact Office
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}